<!--
  L6 — หน้า log การรับส่งข้อมูลกับ MIAW / webhook
  แผนงาน: docs/Social-Api-Log-Plan.md

  โครงหน้ายึดตามโมดูล QA Console (toolbar + qa-card) และตาราง
  ยึดตาม docs/Table-Style-Guide.md เพื่อให้กลมกลืนกับหน้าอื่นในระบบ

  ⚠️ หน้านี้แสดงบทสนทนาของลูกค้าธนาคารจริง เข้าได้เฉพาะ Super admin (roleId 0)
     ด่านจริงอยู่ที่ router.js (SuperAdminRights) และ APIAuthen.cs (listRole = ["0"])
-->
<template>
  <v-container fluid>
    <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
      <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
        <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">
          {{ $t('_socialApiLogTitle') }}
        </v-toolbar-title>
        <v-spacer />
        <div class="d-flex align-center pr-4" style="gap: 8px">
          <v-chip v-if="configLoaded" size="small" :color="config.enabled ? 'success' : 'grey'" variant="flat">
            {{ config.enabled ? $t('_socialApiLogOn') : $t('_socialApiLogOff') }}
          </v-chip>
          <v-chip v-if="configLoaded && config.bodyEnabled" size="small" variant="flat" color="white">
            {{ $t('_socialApiLogRetention', { days: config.retentionDays }) }}
          </v-chip>
        </div>
      </v-toolbar>
    </v-skeleton-loader>

    <!-- แถบสถานะ — บอกทันทีว่ากำลังบันทึกอยู่ไหม ไม่ใช่ให้เดาจากตารางว่าง -->
    <v-alert
      v-if="configLoaded && !config.enabled"
      type="info" variant="tonal" density="compact" class="mt-3 status-alert" icon="mdi-information-outline"
    >
      <div class="text-body-2">{{ $t('_socialApiLogDisabled') }} — {{ $t('_socialApiLogHowToEnable') }}</div>
      <!-- ⚠️ สวิตช์นี้คุมเฉพาะเส้นที่ผ่าน middleware ของ request · การเรียก API เบื้องหลัง
           (sf-transcript) เขียนแถวเสมอโดยตั้งใจ เพราะไม่มีใครเห็นมันด้วยตาเปล่า
           ถ้าไม่บอกไว้ตรงนี้ คนอ่านจะสรุปว่าแถวที่เห็นเป็นของเก่าค้าง แล้วไปกดสวิตช์ที่ไม่เกี่ยวกัน -->
      <div class="text-caption text-medium-emphasis mt-1">{{ $t('_socialApiLogAlwaysOnNote') }}</div>
      <!-- เดิมโชว์คำสั่ง SQL ให้ก๊อปไปรันเอง · ตอนนี้มีสวิตช์ในหน้า Core Configurations แล้ว
           จึงพาไปที่นั่นแทน — ทั้งสองทางแก้แถวเดียวกันใน sy.Dbsettings -->
      <v-btn
        class="mt-2" size="small" variant="tonal" color="info"
        prepend-icon="mdi-tune-variant" to="/setup"
      >
        {{ $t('_socialApiLogOpenSettings') }}
      </v-btn>
    </v-alert>
    <v-alert
      v-else-if="configLoaded && !config.bodyEnabled"
      type="warning" variant="tonal" density="compact" class="mt-3 status-alert" icon="mdi-file-hidden"
    >
      <span class="text-body-2">{{ $t('_socialApiLogMetadataOnly') }}</span>
    </v-alert>

    <!-- ── ตัวกรอง ─────────────────────────────────────────────────────
         จัดเป็น 2 แถวชัด ๆ แทนที่จะให้ทุกอย่างไหลต่อกันแล้วหักบรรทัดเอง
         ของเดิมช่องค้นหาถูกบีบจนป้ายชื่อขาด ("Con…" / "Cor…") ซึ่งอ่านไม่ออกว่าช่องอะไร -->
    <v-card class="qa-card mt-3" variant="flat">
      <v-card-text class="py-3">
        <v-btn-toggle v-model="typeTab" density="comfortable" variant="outlined" divided mandatory class="mb-3">
          <v-btn value="all" size="small">{{ $t('_socialApiLogTabAll') }}</v-btn>
          <v-btn value="miaw-outbound" size="small" prepend-icon="mdi-arrow-top-right-thick">
            {{ $t('_socialApiLogTabOutbound') }}
          </v-btn>
          <v-btn value="webhook-inbound" size="small" prepend-icon="mdi-arrow-bottom-left-thick">
            {{ $t('_socialApiLogTabInbound') }}
          </v-btn>
          <v-btn value="channel-outbound" size="small" prepend-icon="mdi-send-outline">
            {{ $t('_socialApiLogTabChannel') }}
          </v-btn>
          <v-btn value="qa-agent" size="small" prepend-icon="mdi-account-arrow-right-outline">
            {{ $t('_socialApiLogTabQaAgent') }}
          </v-btn>
          <v-btn value="sf-transcript" size="small" prepend-icon="mdi-text-to-speech">
            {{ $t('_socialApiLogTabSfTranscript') }}
          </v-btn>
          <v-btn value="miaw-sse" size="small" prepend-icon="mdi-lightning-bolt-outline">
            {{ $t('_socialApiLogTabSse') }}
          </v-btn>
        </v-btn-toggle>

        <!-- แถวหลัก: เหลือแค่สิ่งที่ใช้บ่อยจริง ๆ
             ช่องค้นหาที่เหลือยุบไว้ใน "ตัวกรองเพิ่มเติม" เพื่อไม่ให้หน้าจอเต็มไปด้วยช่องกรอก -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <v-chip
            size="small"
            :color="filters.onlyErrors ? 'error' : undefined"
            :variant="filters.onlyErrors ? 'flat' : 'outlined'"
            @click="filters.onlyErrors = !filters.onlyErrors; reload()"
          >
            <v-icon start size="small" icon="mdi-alert-circle-outline" />
            {{ $t('_socialApiLogOnlyErrors') }}
          </v-chip>

          <!-- ชิปสรุปตัวกรองที่เปิดอยู่ — ปิดไว้แล้วยังรู้ว่ากรองอะไรค้างอยู่ -->
          <v-chip
            v-for="chip in activeFilterChips" :key="chip.key"
            size="small" variant="tonal" closable
            @click:close="clearFilter(chip.key)"
          >
            {{ chip.label }}
          </v-chip>

          <v-spacer />

          <v-btn
            size="small" variant="text"
            :prepend-icon="showFilters ? 'mdi-chevron-up' : 'mdi-tune'"
            @click="showFilters = !showFilters"
          >
            {{ $t('_socialApiLogMoreFilters') }}
          </v-btn>
          <v-btn size="small" variant="text" icon="mdi-refresh" :loading="loading" @click="reload" />
        </div>

        <v-expand-transition>
          <div v-show="showFilters">
            <v-divider class="my-3" />
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="filters.conversationId"
                  :label="$t('_socialApiLogConversation')"
                  prepend-inner-icon="mdi-forum-outline"
                  density="compact" variant="outlined" clearable hide-details
                  @keyup.enter="reload"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="filters.correlationId"
                  :label="$t('_socialApiLogCorrelation')"
                  prepend-inner-icon="mdi-identifier"
                  density="compact" variant="outlined" clearable hide-details
                  @keyup.enter="reload"
                />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="filters.from" type="datetime-local" :label="$t('_socialApiLogFrom')"
                  density="compact" variant="outlined" hide-details
                />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="filters.to" type="datetime-local" :label="$t('_socialApiLogTo')"
                  density="compact" variant="outlined" hide-details
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex" style="gap: 8px">
                <v-btn color="primary" variant="flat" size="small" :loading="loading" @click="reload">
                  {{ $t('_socialApiLogSearch') }}
                </v-btn>
                <v-btn variant="text" size="small" @click="resetFilters">
                  {{ $t('_socialApiLogClear') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>

      </v-card-text>
    </v-card>

    <!-- ── ตาราง ───────────────────────────────────────────────────────── -->
    <v-card class="qa-card mt-3" variant="flat">
      <v-card-title class="d-flex align-center py-3">
        <span class="text-subtitle-1">{{ $t('_socialApiLogTitle') }}</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ totalCount }} {{ $t('_socialApiLogItems') }}</span>
      </v-card-title>
      <v-divider />

      <div class="qa-table-wrap pt-3">
        <v-data-table-server
          v-model:items-per-page="pageSize"
          v-model:page="page"
          :headers="headers"
          :items="rows"
          :items-length="totalCount"
          :loading="loading"
          :items-per-page-options="[15, 25, 50, 100]"
          class="elevation-0 my-data-table qa-table qa-table-mid"
          density="comfortable"
          fixed-header
          hover
          :row-props="{ class: 'qa-row-click' }"
          @click:row="onRowClick"
          @update:options="onTableOptions"
        >
          <!-- ทิศทาง + operation · ไอคอนเตือนโผล่เฉพาะเมื่อ body ไม่ปกติ
               (ของเดิมมีคอลัมน์ Body ที่เขียน "Captured" ซ้ำทุกแถว = กินที่โดยไม่ให้ข้อมูล) -->
          <template #[`item.logType`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar :color="typeColor(item.logType)" size="30" class="mr-3 flex-shrink-0">
                <v-icon size="16" color="white" :icon="typeIcon(item.logType)" />
              </v-avatar>
              <div class="op-cell">
                <div class="text-body-2 font-weight-medium d-flex align-center">
                  <span class="text-truncate">{{ item.operation }}</span>
                  <v-tooltip v-if="item.bodyState !== 'captured'" :text="$t(`_socialApiLogBody_${item.bodyState}`)" location="top">
                    <template #activator="{ props }">
                      <v-icon v-bind="props" size="14" color="grey" class="ml-1 flex-shrink-0">mdi-file-hidden</v-icon>
                    </template>
                  </v-tooltip>
                  <v-tooltip v-if="item.isTruncated" :text="$t('_socialApiLogTruncated')" location="top">
                    <template #activator="{ props }">
                      <v-icon v-bind="props" size="14" color="warning" class="ml-1 flex-shrink-0">mdi-content-cut</v-icon>
                    </template>
                  </v-tooltip>
                </div>
                <div class="text-caption text-medium-emphasis">{{ item.source }}</div>
              </div>
            </div>
          </template>

          <!-- endpoint · เติมที่ว่างด้วยข้อมูลที่ใช้ไล่ปัญหาได้จริง
               ต่างกันระหว่าง /conversation กับ /message ซึ่งเดิมมองไม่เห็นเลย -->
          <template #[`item.url`]="{ item }">
            <v-tooltip :text="item.url" location="top">
              <template #activator="{ props }">
                <span v-bind="props" class="endpoint text-caption text-medium-emphasis">
                  {{ shortPath(item.url) }}
                </span>
              </template>
            </v-tooltip>
          </template>

          <template #[`item.createDate`]="{ item }">
            <div class="text-body-2 text-no-wrap">{{ clockTime(item.createDate) }}</div>
            <div class="text-caption text-medium-emphasis text-no-wrap">{{ dayLabel(item.createDate) }}</div>
          </template>

          <template #[`item.statusCode`]="{ item }">
            <span class="d-inline-flex align-center text-no-wrap">
              <v-icon size="10" :color="statusColor(item)" class="mr-2">mdi-circle</v-icon>
              <span class="qa-num text-body-2 font-weight-medium">
                {{ item.statusCode === null ? 'ERR' : item.statusCode }}
              </span>
              <v-tooltip v-if="item.error" :text="item.error" location="top">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="14" color="error" class="ml-1">mdi-information-outline</v-icon>
                </template>
              </v-tooltip>
            </span>
          </template>

          <!-- ตัวเลขอยู่บรรทัดเดียวกับแท่งเสมอ · ของเดิม "2422 ms" หักเป็นสองบรรทัด -->
          <template #[`item.durationMs`]="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear
                :model-value="durationPercent(item.durationMs)"
                :color="durationColor(item.durationMs)"
                height="7" rounded bg-color="grey-lighten-3" style="width: 72px"
              />
              <span class="qa-num text-caption text-medium-emphasis ml-2 dur-value">{{ item.durationMs }} ms</span>
            </div>
          </template>

          <template #[`item.conversationID`]="{ item }">
            <v-tooltip v-if="item.conversationID" :text="item.conversationID" location="top">
              <template #activator="{ props }">
                <v-chip
                  v-bind="props" size="small" variant="flat" label
                  :color="conversationColor(item.conversationID)"
                  class="qa-num"
                  @click.stop="filterByConversation(item.conversationID)"
                >
                  {{ item.conversationID.slice(0, 8) }}
                </v-chip>
              </template>
            </v-tooltip>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template #[`item.actions`]>
            <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <div class="text-center py-10">
              <v-icon size="46" color="grey-lighten-1">mdi-tray-remove</v-icon>
              <div class="text-subtitle-1 mt-3">
                {{ config.enabled ? $t('_socialApiLogEmptyOn') : $t('_socialApiLogEmptyOff') }}
              </div>
            </div>
          </template>
        </v-data-table-server>
      </div>
    </v-card>

    <!-- ── รายละเอียด: timeline ของทุกแถวที่ใช้ correlation เดียวกัน ────────── -->
    <v-dialog v-model="detailOpen" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ $t('_socialApiLogDetail') }}</span>
          <v-chip v-if="timeline.length > 1" size="small" class="ml-2" variant="tonal">
            {{ $t('_socialApiLogSteps', { n: timeline.length }) }}
          </v-chip>
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="detailOpen = false" />
        </v-card-title>

        <v-card-text>
          <!-- 1 correlation = ได้หลายแถวจริง (id เดียวคร่อมหลาย operation + retry ตอน 401)
               จึงแสดงเป็นลำดับเวลา ไม่ใช่แถวเดียว -->
          <v-expansion-panels v-model="openedPanels" multiple>
            <v-expansion-panel v-for="(row, i) in timeline" :key="row.socialApiLogID">
              <v-expansion-panel-title>
                <div class="d-flex align-center flex-wrap" style="gap: 8px">
                  <v-chip size="x-small" variant="tonal">{{ i + 1 }}</v-chip>
                  <strong>{{ row.operation }}</strong>
                  <v-chip size="x-small" :color="statusColor(row)" variant="tonal">
                    {{ row.statusCode === null ? '—' : row.statusCode }}
                  </v-chip>
                  <span class="text-caption">{{ row.durationMs }} ms</span>
                  <span class="text-caption text-medium-emphasis">{{ formatDate(row.createDate) }}</span>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-row dense class="mb-2">
                  <v-col cols="12" md="6"><b>Direction</b> · {{ row.direction }}</v-col>
                  <v-col cols="12" md="6"><b>Source</b> · {{ row.source }}</v-col>
                  <v-col cols="12" md="6"><b>Method</b> · {{ row.method }}</v-col>
                  <v-col cols="12"><b>URL</b> · <span class="text-break">{{ row.url }}</span></v-col>
                  <v-col cols="12" md="6"><b>Correlation ID</b> · {{ row.correlationID || '—' }}</v-col>
                  <!-- correlation ของ Salesforce เอง — ใช้จับคู่กับ event log ฝั่งเขาตอนเปิดเคส -->
                  <v-col cols="12" md="6"><b>SF Correlation ID</b> · {{ row.sfCorrelationID || '—' }}</v-col>
                  <v-col cols="12"><b>Conversation ID</b> · {{ row.conversationID || '—' }}</v-col>
                  <v-col v-if="row.error" cols="12" class="text-error"><b>Error</b> · {{ row.error }}</v-col>
                </v-row>

                <v-alert v-if="row.bodyState !== 'captured'" type="info" variant="tonal" density="compact" class="mb-2">
                  {{ $t(`_socialApiLogBody_${row.bodyState}`) }}
                </v-alert>
                <v-alert v-if="row.isTruncated" type="warning" variant="tonal" density="compact" class="mb-2">
                  {{ $t('_socialApiLogTruncated') }}
                </v-alert>

                <json-block :title="'Request Headers'" :value="row.requestHeaders" />
                <json-block :title="'Request Body'" :value="row.requestBody" />
                <json-block :title="'Response Headers'" :value="row.responseHeaders" />
                <json-block :title="'Response Body'" :value="row.responseBody" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
import QaBar from '@/components/QAConsole/QaBar.vue';
// สไตล์ร่วม (.qa-card, .qa-table) — โหลดเองเพราะไม่ได้ใช้ QaPageShell
import '@/assets/qa-console.css';

/** กล่องแสดง JSON พร้อมปุ่มคัดลอก — จัดรูปให้อ่านง่ายถ้า parse ได้ */
const JsonBlock = {
  name: 'JsonBlock',
  props: { title: String, value: [String, Object] },
  data: () => ({ copied: false }),
  computed: {
    pretty() {
      if (this.value === null || this.value === undefined || this.value === '') return '';
      if (typeof this.value !== 'string') return JSON.stringify(this.value, null, 2);
      try {
        return JSON.stringify(JSON.parse(this.value), null, 2);
      } catch {
        // ไม่ใช่ JSON (เช่น form-encoded ของ token endpoint) — แสดงดิบ ๆ
        return this.value;
      }
    },
  },
  methods: {
    async copy() {
      try {
        await navigator.clipboard.writeText(this.pretty);
        this.copied = true;
        setTimeout(() => (this.copied = false), 1500);
      } catch {
        this.copied = false;
      }
    },
  },
  template: `
    <div class="mb-3">
      <div class="d-flex align-center mb-1">
        <span class="text-caption font-weight-bold">{{ title }}</span>
        <v-spacer />
        <v-btn v-if="pretty" size="x-small" variant="text"
               :prepend-icon="copied ? 'mdi-check' : 'mdi-content-copy'" @click="copy">
          {{ copied ? 'copied' : 'copy' }}
        </v-btn>
      </div>
      <pre v-if="pretty" class="json-block">{{ pretty }}</pre>
      <div v-else class="text-caption text-medium-emphasis">—</div>
    </div>
  `,
};

export default {
  name: 'SocialApiLog',
  // toolbar ธีมเดียวกับหน้าอื่นในระบบ — ค่ามาจาก provide ของ App.vue
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  components: { JsonBlock, QaBar },

  data() {
    return {
      loading: false,
      configLoaded: false,
      typeTab: 'all',
      showFilters: false,
      config: { enabled: false, bodyEnabled: false, retentionDays: 7 },

      rows: [],
      totalCount: 0,
      page: 1,
      pageSize: 25,

      filters: {
        // ⚠️ ไม่ติ๊ก miaw-sse เป็นค่าเริ่มต้น — แถวพวกนั้นเป็น event ไม่ใช่ API call จริง
        //    (duration 0 ตายตัว) agent ตอบ 1 ครั้ง = 1 แถว จะกลบแถวที่มีประโยชน์
        //    แต่ qa-agent ติ๊กไว้ เพราะมีแค่ 1-2 แถวต่อเคส และเป็นแถวที่ต้องเปิดดูตอนสงสัยว่า
        //    "ทำไมเคสไม่มีเจ้าของ" — ต้องหาเจอโดยไม่ต้องรู้ว่ามีตัวกรองซ่อนอยู่
        logTypes: ['miaw-outbound', 'webhook-inbound', 'channel-outbound', 'qa-agent', 'sf-transcript'],
        correlationId: '',
        conversationId: '',
        from: '',
        to: '',
        onlyErrors: false,
      },

      detailOpen: false,
      timeline: [],
      openedPanels: [],
    };
  },

  computed: {
    /// ตัวกรองที่เปิดอยู่ — แสดงเป็นชิปให้เห็นแม้พับ panel ไว้
    /// ไม่งั้นผู้ใช้จะงงว่าทำไมผลลัพธ์น้อยผิดปกติ ทั้งที่กรองค้างไว้จากครั้งก่อน
    activeFilterChips() {
      const f = this.filters;
      const chips = [];
      if (f.conversationId) chips.push({ key: 'conversationId', label: `${this.$t('_socialApiLogConversation')}: ${f.conversationId.slice(0, 12)}` });
      if (f.correlationId) chips.push({ key: 'correlationId', label: `${this.$t('_socialApiLogCorrelation')}: ${f.correlationId.slice(0, 10)}` });
      if (f.from) chips.push({ key: 'from', label: `${this.$t('_socialApiLogFrom')} ${f.from.replace('T', ' ')}` });
      if (f.to) chips.push({ key: 'to', label: `${this.$t('_socialApiLogTo')} ${f.to.replace('T', ' ')}` });
      return chips;
    },

    logTypeOptions() {
      return [
        { value: 'miaw-outbound', label: this.$t('_socialApiLogTypeOutbound') },
        { value: 'channel-outbound', label: this.$t('_socialApiLogTabChannel') },
        { value: 'webhook-inbound', label: this.$t('_socialApiLogTypeInbound') },
        { value: 'qa-agent', label: this.$t('_socialApiLogTypeQaAgent') },
        { value: 'sf-transcript', label: this.$t('_socialApiLogTypeSfTranscript') },
        { value: 'miaw-sse', label: this.$t('_socialApiLogTypeSse') },
      ];
    },
    headers() {
      return [
        // ⚠️ operation กว้างคงที่ · ของเดิมเป็นคอลัมน์เดียวที่ยืดได้ เลยกินไป ~40% ของตาราง
        //    แล้วดันคอลัมน์ที่เหลือไปเบียดกันทางขวา
        { title: this.$t('_socialApiLogOperation'), key: 'logType', sortable: false, width: 230 },
        { title: 'Endpoint', key: 'url', sortable: false },
        { title: this.$t('_socialApiLogTime'), key: 'createDate', sortable: false, width: 110 },
        { title: 'Status', key: 'statusCode', sortable: false, width: 100 },
        { title: this.$t('_socialApiLogDuration'), key: 'durationMs', sortable: false, width: 165 },
        { title: this.$t('_socialApiLogConversation'), key: 'conversationID', sortable: false, width: 120 },
        { title: '', key: 'actions', sortable: false, align: 'end', width: 48 },
      ];
    },
  },

  watch: {
    typeTab(value) {
      // แท็บ "ทั้งหมด" = ไม่รวม SSE (แถว SSE เป็น event ไม่ใช่ API call จริง)
      //
      // ⚠️ รายการนี้ต้องตรงกับค่าเริ่มต้นของ filters.logTypes — ถ้าลืมเติมชนิดใหม่ตรงนี้
      //    ผู้ใช้จะเห็นแถวตอนเปิดหน้าครั้งแรก แล้ว "หายไป" ทันทีที่กดแท็บทั้งหมด
      //    ซึ่งอ่านได้ว่าระบบไม่ได้ทำงาน · qa-agent ไม่อยู่ในรายการนี้มาแต่เดิม (ต่างจากค่าเริ่มต้น)
      this.filters.logTypes = value === 'all'
        ? ['miaw-outbound', 'webhook-inbound', 'channel-outbound', 'sf-transcript']
        : [value];
      this.page = 1;
      this.reload();
    },
  },

  mounted() {
    this.loadConfig();

    // deep link ?audit={correlationId} — ลิงก์ที่ agent ได้จากแชทตอนอัปไฟล์ล้มเหลว
    // (AuditViewerUrl() ใน SalesforceMiawClient สร้างไว้ให้)
    const audit = this.$route.query.audit;
    if (audit) {
      this.filters.correlationId = String(audit);
      this.openTimeline({ correlationID: String(audit) });
    }

    // ⚠️ ไม่เรียก reload() ตรงนี้ — v-data-table-server ยิง update:options ตอน mount
    //    ซึ่งไปเรียก reload() ให้เอง ถ้าเรียกซ้ำจะโหลด 2 รอบทุกครั้งที่เปิดหน้า
    //    (รวม COUNT บนตารางใหญ่ซึ่งเป็น query ที่แพงที่สุดของหน้านี้)
  },

  methods: {
    loadConfig() {
      this.$call.GetSocialApiLogConfig(
        (res) => {
          if (res && res.data) this.config = res.data;
          this.configLoaded = true;
        },
        () => {
          this.configLoaded = true;
        },
      );
    },

    reload() {
      this.loading = true;

      const request = {
        logTypes: this.filters.logTypes && this.filters.logTypes.length ? this.filters.logTypes : null,
        correlationId: this.filters.correlationId || null,
        conversationId: this.filters.conversationId || null,
        // ⚠️ ส่ง "เวลาที่คนเลือก" ตรง ๆ ไม่แปลงเป็น UTC · server แปลงให้เองด้วย
        //    RegionsLocalDateTime · ถ้าแปลงที่นี่ด้วยจะได้ค่าตาม TZ ของเบราว์เซอร์
        //    ซึ่งคนที่เครื่องตั้ง TZ ต่างกันจะค้นได้คนละช่วง
        from: this.filters.from ? moment(this.filters.from).format('YYYY-MM-DDTHH:mm:ss') : null,
        to: this.filters.to ? moment(this.filters.to).format('YYYY-MM-DDTHH:mm:ss') : null,
        onlyErrors: this.filters.onlyErrors ? true : null,
        page: this.page,
        pageSize: this.pageSize,
      };

      this.$call.SearchSocialApiLog(
        request,
        (res) => {
          const data = (res && res.data) || {};
          this.rows = data.items || [];
          this.totalCount = data.totalCount || 0;
          this.loading = false;
        },
        (err) => {
          console.error('SearchSocialApiLog failed:', err);
          this.rows = [];
          this.totalCount = 0;
          this.loading = false;
        },
      );
    },

    resetFilters() {
      this.filters = {
        logTypes: ['miaw-outbound', 'webhook-inbound', 'channel-outbound', 'qa-agent', 'sf-transcript'],
        correlationId: '',
        conversationId: '',
        from: '',
        to: '',
        onlyErrors: false,
      };
      this.typeTab = 'all';
      this.page = 1;
      this.reload();
    },

    openTimeline(item) {
      this.timeline = [];
      this.openedPanels = [];
      this.detailOpen = true;

      if (item.correlationID) {
        this.$call.GetSocialApiLogTimeline(
          item.correlationID,
          (res) => {
            this.timeline = (res && res.data) || [];
            // กางอันแรกให้เลย — ส่วนใหญ่มีแถวเดียว การต้องกดอีกทีไม่มีประโยชน์
            this.openedPanels = this.timeline.length ? [0] : [];
          },
          () => this.loadSingle(item.socialApiLogID),
        );
      } else {
        this.loadSingle(item.socialApiLogID);
      }
    },

    /** แถวที่ไม่มี correlation (เช่น webhook ที่ถูกปฏิเสธ) — ดึงทีละแถว */
    loadSingle(id) {
      if (!id) return;
      this.$call.GetSocialApiLogById(
        id,
        (res) => {
          this.timeline = res && res.data ? [res.data] : [];
          this.openedPanels = this.timeline.length ? [0] : [];
        },
        () => {
          this.timeline = [];
        },
      );
    },

    onRowClick(_event, { item }) {
      this.openTimeline(item);
    },

    onTableOptions({ page, itemsPerPage }) {
      this.page = page;
      this.pageSize = itemsPerPage;
      this.reload();
    },

    clearFilter(key) {
      this.filters[key] = '';
      this.page = 1;
      this.reload();
    },

    filterByConversation(id) {
      this.filters.conversationId = id;
      this.page = 1;
      this.reload();
    },


    /// วันของแถว — แสดงแค่ "วันนี้ / เมื่อวาน / วันที่" ไม่ใช่ "an hour ago" ทุกแถว
    /// (ของเดิมทุกแถวขึ้นเหมือนกันหมดจนไม่ให้ข้อมูลอะไร)
    dayLabel(value) {
      if (!value) return '';
      // ⚠️ server แปลงเป็นเวลาธนาคารมาให้แล้ว (SocialApiLogBAL) — ห้าม .utc().local() ซ้ำ
      //    ไม่งั้นจะบวก offset สองรอบ
      const d = moment(value);
      if (d.isSame(moment(), 'day')) return this.$t('_socialApiLogTodayShort');
      if (d.isSame(moment().subtract(1, 'day'), 'day')) return this.$t('_socialApiLogYesterday');
      return d.format('DD/MM/YYYY');
    },

    /// ตัด host ออก เหลือเฉพาะ path ที่ต่างกันจริงระหว่าง operation
    shortPath(url) {
      if (!url) return '—';
      try {
        return new URL(url).pathname;
      } catch {
        return url;
      }
    },

    clockTime(value) {
      return value ? moment(value).format('HH:mm:ss.SSS') : '';
    },

    /// แท่งเทียบ duration — สเกล log เพราะค่าจริงกระจายตั้งแต่ 40ms ถึงหลายวินาที
    /// สเกล log เพราะค่าจริงกระจายตั้งแต่ 40ms ถึงหลายวินาที
    durationPercent(ms) {
      const capped = Math.min(Math.max(ms, 1), 5000);
      return Math.round((Math.log10(capped) / Math.log10(5000)) * 100);
    },

    durationColor(ms) {
      if (ms >= 3000) return 'error';
      if (ms >= 1000) return 'warning';
      return 'success';
    },

    /// สีประจำบทสนทนา — แถวของบทสนทนาเดียวกันได้สีเดียวกันเสมอ
    /// ทำให้ตาจับกลุ่มได้เองโดยไม่ต้อง group จริง (ซึ่งทำไม่ได้เพราะแถว inbound ไม่มี id)
    conversationColor(id) {
      const palette = ['indigo', 'teal', 'deep-orange', 'purple', 'green', 'blue-grey', 'brown', 'cyan'];
      let hash = 0;
      for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) % 997;
      return palette[hash % palette.length];
    },


    typeIcon(t) {
      if (t === 'miaw-outbound') return 'mdi-arrow-top-right-thick';
      if (t === 'webhook-inbound') return 'mdi-arrow-bottom-left-thick';
      if (t === 'channel-outbound') return 'mdi-send-outline';
      if (t === 'qa-agent') return 'mdi-account-arrow-right-outline';
      // INT-02 — ส่งข้อความที่ถอดเสียงได้ขึ้น Salesforce (เข้ารหัสก่อนส่ง)
      if (t === 'sf-transcript') return 'mdi-text-to-speech';
      return 'mdi-lightning-bolt-outline';
    },

    formatDate(value) {
// ⚠️ server แปลงเป็นเวลาธนาคาร (AppSettings.RegionsLocalDateTime) มาให้แล้ว
      //    **ห้ามแปลงซ้ำที่นี่** · เดิมใช้ moment.utc().local() ซึ่งถูกตอนนั้นเพราะ server
      //    ส่ง UTC ดิบมา แต่ตอนนี้จะกลายเป็นบวก offset สองรอบ
      //
      //    ที่ย้ายไปแปลงฝั่ง server เพราะ .local() ใช้ TZ ของเบราว์เซอร์ ทีม QA ที่เครื่อง
      //    ตั้งเวลาต่างกันจะเห็นคนละเวลาบนแถวเดียวกัน
      return value ? moment(value).format('DD/MM/YYYY HH:mm:ss.SSS') : '-';
    },

    typeColor(t) {
      if (t === 'miaw-outbound') return 'indigo';
      if (t === 'webhook-inbound') return 'teal';
      if (t === 'channel-outbound') return 'deep-purple';
      if (t === 'qa-agent') return 'orange-darken-2';
      if (t === 'sf-transcript') return 'cyan-darken-2';
      return 'grey';
    },

    statusColor(row) {
      if (row.error) return 'error';
      if (row.statusCode === null || row.statusCode === undefined) return 'error';
      if (row.statusCode >= 200 && row.statusCode < 300) return 'success';
      if (row.statusCode >= 400) return 'error';
      return 'warning';
    },


  },
};
</script>

<style scoped>
.title {
  font-size: 18px;
  font-weight: 500;
}

.status-alert {
  border-radius: 12px;
}

.status-alert code {
  background: rgba(128, 128, 128, 0.12);
  border-radius: 4px;
  padding: 2px 6px;
  white-space: pre-wrap;
  word-break: break-all;
}

/* operation ต้องไม่ดันคอลัมน์อื่น — ตัดด้วย ellipsis แทนการขยาย */
.op-cell {
  min-width: 0;
}

.endpoint {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ตัวเลข ms ห้ามหักบรรทัด และกว้างคงที่เพื่อให้แท่งเรียงตรงกันทุกแถว */
.dur-value {
  white-space: nowrap;
  min-width: 62px;
  text-align: right;
}

/* ตารางตาม docs/Table-Style-Guide.md */
:deep(.my-data-table .v-table__wrapper) {
  max-height: 560px !important;
  overflow: auto !important;
}

.json-block {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 8px 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.text-break {
  word-break: break-all;
}
</style>
